// src/ui/overlays/SettingsPanel.tsx — UI-06 settings menu (D-01/02/03/04).
//
// A standalone, focus-trapped modal dialog (the PublishModal idiom,
// src/ui/admin/publish.tsx — Escape close + focus return). Five rows:
//   - Master / Music / Sound effects volume sliders (0..100%, read-out `{n}%`)
//   - Joystick size segmented control (Small / Medium / Large)
//   - Vibration toggle — capability-gated: rendered ONLY where
//     supportsVibration() (`'vibrate' in navigator`) is true (D-03; iOS Safari
//     omits the row entirely — never a broken control).
//
// Live audio gains: every volume-slider change calls
// audioManager.setVolumes({master,music,sfx}) (0..1) so the music score and the
// SFX world respond instantly (D-01 bus split, Plan 07-01 seam), and persists
// the new blob via the settings store (saveSettings → `hunter:settings`,
// Plan 07-03). Joystick + vibration likewise persist on change.
//
// Two entry modes (D-02) via the `inGame` prop:
//   - from-title (inGame=false): opaque #0d1f2d ground, `Done` ghost close.
//   - in-game   (inGame=true):   dim+blur backdrop, `×` close that ALSO emits
//     renderBus 'resume-requested' so the sim resumes on close (Escape too).
//
// ZERO new design tokens (07-UI-SPEC: P0–P6 palette/sizes only). Critical
// layout values are mirrored inline so jsdom specs assert without the
// stylesheet (the TorchButton / RoundTimer convention). This component is
// STANDALONE — App.tsx wiring lands in Plan 10.
//
// SOURCE: 07-UI-SPEC.md §Surface 2 + §Copywriting Contract + §Interaction
//         States; 07-PATTERNS.md §SettingsPanel.

import { useCallback, useEffect, useRef, useState, type JSX } from 'react';

import { renderBus } from '../../render/bus';
import { audioManager } from '../../render/audio/manager';
import {
  getSettings,
  saveSettings,
  type JoystickSize,
  type Settings,
} from '../settings/store';
import { supportsVibration } from '../settings/haptics';

import './settings-panel.css';

export interface SettingsPanelProps {
  /** In-game entry (over a paused, dimmed canvas) vs from-title (opaque). */
  inGame: boolean;
  onClose: () => void;
}

type VolumeKey = 'master' | 'music' | 'sfx';

const VOLUME_ROWS: ReadonlyArray<{ key: VolumeKey; label: string }> = [
  { key: 'master', label: 'Master volume' },
  { key: 'music', label: 'Music' },
  { key: 'sfx', label: 'Sound effects' },
];

const JOYSTICK_OPTIONS: ReadonlyArray<{ value: JoystickSize; label: string }> = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

/** 0..1 gain → integer percent read-out. */
function pct(v: number): number {
  return Math.round(v * 100);
}

export function SettingsPanel({ inGame, onClose }: SettingsPanelProps): JSX.Element {
  // Local mirror of the persisted blob; seeded once from the store so the
  // sliders are controlled and the read-outs stay in sync within the session.
  const [settings, setSettings] = useState<Settings>(() => ({ ...getSettings() }));
  const dialogRef = useRef<HTMLDivElement | null>(null);
  // Capture the trigger so focus returns to it on close (gear / pause button).
  const triggerRef = useRef<Element | null>(
    typeof document !== 'undefined' ? document.activeElement : null,
  );

  // Single close path: in-game also resumes the sim (D-02). Escape + the ×
  // button + (in-game) backdrop tap all funnel through here.
  const handleClose = useCallback((): void => {
    if (inGame) renderBus.emit('resume-requested', { source: 'menu' });
    // Return focus to the opener (PublishModal focus-return contract).
    const trigger = triggerRef.current;
    if (trigger instanceof HTMLElement) trigger.focus();
    onClose();
  }, [inGame, onClose]);

  // Escape closes (PublishModal idiom — window-level so input focus is kept).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }
      // Focus trap: keep Tab within the dialog (ARIA aria-modal contract).
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, input, [href], [tabindex]:not([tabindex="-1"])',
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first || !last) return;
        const active = document.activeElement;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return (): void => window.removeEventListener('keydown', onKey);
  }, [handleClose]);

  // Initial focus lands inside the dialog (aria-modal contract).
  useEffect(() => {
    dialogRef.current?.querySelector<HTMLElement>('button, input')?.focus();
  }, []);

  const onVolumeChange = useCallback(
    (key: VolumeKey, percent: number): void => {
      const v = Math.min(1, Math.max(0, percent / 100));
      const next: Settings = { ...settings, [key]: v };
      setSettings(next);
      // Live gains — merged {master,music,sfx} as 0..1 (Plan 07-01 seam).
      audioManager.setVolumes({
        master: next.master,
        music: next.music,
        sfx: next.sfx,
      });
      // Persist (Plan 07-03 store; fail-open).
      saveSettings({ [key]: v });
    },
    [settings],
  );

  const onJoystickChange = useCallback((value: JoystickSize): void => {
    setSettings((s) => ({ ...s, joystickSize: value }));
    saveSettings({ joystickSize: value });
  }, []);

  const onVibrationToggle = useCallback((): void => {
    setSettings((s) => {
      const vibration = !s.vibration;
      saveSettings({ vibration });
      return { ...s, vibration };
    });
  }, []);

  const showVibration = supportsVibration();

  return (
    <div
      className={inGame ? 'settings-panel settings-panel--ingame' : 'settings-panel'}
      data-testid="settings-panel-backdrop"
      onClick={inGame ? handleClose : undefined}
      // Mirror critical layout inline for jsdom (TorchButton/RoundTimer idiom).
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 190,
        display: 'flex',
        flexDirection: 'column',
        background: inGame ? 'rgba(13, 31, 45, 0.7)' : '#0d1f2d',
        backdropFilter: inGame ? 'blur(12px)' : undefined,
      }}
    >
      <div
        ref={dialogRef}
        className="settings-panel__dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        data-testid="settings-panel"
        onClick={(e): void => e.stopPropagation()}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 190,
          display: 'flex',
          flexDirection: 'column',
          padding: 'calc(env(safe-area-inset-top) + 32px) 24px',
          color: '#f0ead8',
          fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        }}
      >
        <div className="settings-panel__header">
          <h2 className="settings-panel__title">Settings</h2>
          {inGame ? (
            <button
              type="button"
              className="settings-panel__close settings-panel__close--icon"
              data-testid="settings-close"
              aria-label="Close"
              onClick={handleClose}
            >
              ×
            </button>
          ) : (
            <button
              type="button"
              className="settings-panel__close settings-panel__close--done"
              data-testid="settings-close"
              onClick={handleClose}
            >
              Done
            </button>
          )}
        </div>

        <div className="settings-panel__list">
          {VOLUME_ROWS.map(({ key, label }) => {
            const percent = pct(settings[key]);
            return (
              <div className="settings-row settings-row--slider" key={key}>
                <div className="settings-row__top">
                  <span className="settings-row__label">{label}</span>
                  <span
                    className="settings-row__readout"
                    data-testid={`settings-readout-${key}`}
                  >
                    {percent}%
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={percent}
                  className="settings-slider"
                  data-testid={`settings-slider-${key}`}
                  role="slider"
                  aria-label={label}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={percent}
                  onChange={(e): void => onVolumeChange(key, Number(e.target.value))}
                  // amber-fill idiom (stamina-bar --fill-pct).
                  style={{ ['--fill-pct' as 'width']: `${percent}%` }}
                />
              </div>
            );
          })}

          <div className="settings-row settings-row--segmented">
            <span className="settings-row__label">Joystick size</span>
            <div
              className="settings-segmented"
              role="group"
              aria-label="Joystick size"
            >
              {JOYSTICK_OPTIONS.map(({ value, label }) => {
                const active = settings.joystickSize === value;
                return (
                  <button
                    type="button"
                    key={value}
                    className={
                      active
                        ? 'settings-segmented__seg settings-segmented__seg--active'
                        : 'settings-segmented__seg'
                    }
                    data-testid={`joystick-size-${value}`}
                    aria-pressed={active}
                    onClick={(): void => onJoystickChange(value)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {showVibration && (
            <div className="settings-row settings-row--toggle">
              <div className="settings-row__toggle-text">
                <span className="settings-row__label">Vibration</span>
                <span className="settings-row__desc">Buzz on catches and calls.</span>
              </div>
              <button
                type="button"
                className={
                  settings.vibration
                    ? 'settings-toggle settings-toggle--on'
                    : 'settings-toggle'
                }
                data-testid="settings-vibration-toggle"
                role="switch"
                aria-checked={settings.vibration}
                aria-label="Vibration"
                onClick={onVibrationToggle}
              >
                <span className="settings-toggle__knob" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
